import React, { ReactNode } from "react";
import { Grid } from "@mui/material";

export interface DetailLayoutProps {
  /** Content for the left panel (overview card, info summary, etc.) */
  leftContent: ReactNode;
  /** Content for the right panel (tabs, detail views, etc.) */
  rightContent: ReactNode;
  /** Grid width for the left panel (MUI Grid md breakpoint). Default: 2.6 */
  leftWidth?: number;
  /** Whether to show the right panel. Default: true */
  showRightPanel?: boolean;
  /**
   * When true, the layout fills its parent's height and uses overflow scrolling.
   * Use for full-page contexts (e.g. PatientLayout).
   * When false, height flows naturally from content.
   * Use for embedded contexts (e.g. inside Admin page).
   * Default: false
   */
  fullHeight?: boolean;
  /** Optional additional sx for the outer container */
  sx?: Record<string, any>;
}

/**
 * Shared two-panel detail layout used by both Patient and User pages.
 *
 * Left panel: narrow column for overview/summary info
 * Right panel: fills remaining space for tabs and detail content
 *
 * Pattern extracted from PatientLayout — keeps both layouts consistent.
 */
const DetailLayout: React.FC<DetailLayoutProps> = ({
  leftContent,
  rightContent,
  leftWidth = 2.6,
  showRightPanel = true,
  fullHeight = false,
  sx,
}) => {
  return (
    <Grid
      container
      direction="column"
      sx={{
        width: "100%",
        ...(fullHeight ? { height: "100%" } : {}),
        flexWrap: "nowrap",
        ...sx,
      }}
    >
      <Grid
        item
        xs={fullHeight ? true : undefined}
        sx={{
          display: "flex",
          padding: "0px",
          ...(fullHeight ? { overflowY: "auto", overflowX: "auto" } : {}),
        }}
      >
        <Grid
          container
          sx={{ display: "flex", width: "100%", gap: "10px" }}
        >
          {/* Left Panel */}
          <Grid
            item
            xs={12}
            md={showRightPanel ? leftWidth : 12}
            sx={{ alignItems: "stretch" }}
          >
            {leftContent}
          </Grid>

          {/* Right Panel */}
          {showRightPanel && (
            // minWidth: 0 — a flex item's default min-width is its CONTENT's
            // intrinsic width, so a tab holding a wide table (Rechnungen)
            // couldn't shrink to fit beside the left column and the container
            // wrapped it below on small screens. With 0 the panel always keeps
            // its place and over-wide content scrolls inside its own wrapper.
            <Grid item xs={12} md sx={{ minWidth: 0 }}>
              {rightContent}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default DetailLayout;
